import { useEffect, useState } from 'react';

import { getSizeConversion } from 'shared/services/sizeConversion';
import { IConvertSize } from 'shared/types/ConvertSize';
import { ISize } from 'shared/types/Size';
import { startCase } from 'shared/utils/startCase';
import { t } from 'localization/utils/t';
import { useGenderLabel } from 'shared/hooks/useGenderLabel';
import { useStoreSelector } from 'store/hooks/storeHooks';

const TP = 'shared.hooks.useGenderSizingLabel';

export const useGenderSizingLabel = (size: ISize) => {
  const [isLoading, setIsLoading] = useState(true);
  const { isFashionProduct, productType, singleGender, sizeBrand, sizeUnit } = useStoreSelector(
    (state) => state.pdp.productTemplate,
  );
  const getGenderLabel = useGenderLabel();
  const conversionFromTitle = t(`${TP}.conversionFromTitle`, '{unit} {gender} Size {size}', {
    unit: sizeUnit.toUpperCase(),
    gender: startCase(getGenderLabel(singleGender), { capitalizeInOtherLanguages: false }),
    size: size?.presentation?.toUpperCase(),
  });

  const defaultTitle = t(`${TP}.defeaultTitle`, '{unit} {gender} Sizes', {
    unit: sizeUnit.toUpperCase(),
    gender: startCase(getGenderLabel(singleGender), { capitalizeInOtherLanguages: false }),
  });

  const defaultApparelTitle = t(`${TP}.defaultApparelTitle`, '{gender} Sizes', {
    gender: startCase(getGenderLabel(singleGender), {
      capitalizeInOtherLanguages: false,
    }),
  });

  const [genderSizeLabel, setGenderSizeLabel] = useState(
    isFashionProduct ? defaultApparelTitle : defaultTitle,
  );
  const { user } = useStoreSelector((state) => state.auth);
  const isApparel = isFashionProduct || (productType && productType !== 'sneakers');
  const {
    sizeBrand: userSizeBrand,
    sizeGender: userSizeGender,
    convertedSizeUnit: userSizeUnit,
  } = user || {};

  useEffect(() => {
    const selectedSize = String(size?.value);
    const fromBrand = sizeBrand;
    const toBrand = sizeBrand || 'default';
    const fromGender = singleGender?.toUpperCase();
    const toGender = userSizeGender?.toUpperCase();
    const fromUnit = sizeUnit?.toUpperCase();
    const toUnit = userSizeUnit?.toUpperCase();
    const getConvertedSize = async () => {
      try {
        setIsLoading(true);
        let convertedSize: IConvertSize;

        /* There is a bug in the API for some sizes (ex Adidas W4.5 US will convert to W4) */
        if (fromBrand === toBrand && fromGender === toGender && fromUnit === toUnit) {
          convertedSize = { size: selectedSize, unit: fromUnit, gender: fromGender };
        } else {
          const result: IConvertSize = await getSizeConversion({
            userSize: selectedSize,
            fromBrand,
            fromGender,
            toGender,
            fromUnit,
            toUnit,
            productType: 'SNEAKERS',
          });

          if (!result) throw new Error('Size conversion failed');
          convertedSize = result;
        }

        if (
          convertedSize?.unit?.toUpperCase() !== fromUnit ||
          convertedSize?.gender?.toUpperCase() !== fromGender ||
          convertedSize?.size !== String(size.value)
        ) {
          const userTitle = t(`${TP}.userTitle`, '{unit} {gender} Size {size}', {
            unit: convertedSize?.unit?.toUpperCase(),
            gender: startCase(getGenderLabel(convertedSize?.gender), {
              capitalizeInOtherLanguages: false,
            }),
            size: convertedSize?.size,
          });
          setGenderSizeLabel(`${conversionFromTitle} / ${userTitle}`);
        } else {
          setGenderSizeLabel(defaultTitle);
        }
      } catch {
        setGenderSizeLabel(defaultTitle);
      } finally {
        setIsLoading(false);
      }
    };

    if (
      !isApparel &&
      userSizeGender &&
      userSizeUnit &&
      size &&
      (sizeBrand?.toUpperCase() !== userSizeBrand?.toUpperCase() ||
        fromGender !== toGender ||
        fromUnit !== toUnit)
    ) {
      void getConvertedSize();
    } else {
      setGenderSizeLabel(isFashionProduct ? defaultApparelTitle : defaultTitle);
      setIsLoading(false);
    }
    return () => {
      setIsLoading(false);
    };
  }, [
    conversionFromTitle,
    defaultApparelTitle,
    defaultTitle,
    getGenderLabel,
    isApparel,
    isFashionProduct,
    singleGender,
    size,
    sizeBrand,
    sizeUnit,
    userSizeBrand,
    userSizeGender,
    userSizeUnit,
  ]);

  return {
    isLoading,
    genderSizeLabel,
  };
};
